import React from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import CloudUpload from '@mui/icons-material/CloudUpload';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import { useUploadContext } from '../../contexts/UploadContext';

/**
 * Persistent upload indicator in the app toolbar. Visible on every page so
 * uploads started on the Upload page stay observable after navigating away —
 * failures are announced instead of dying silently off-screen.
 */
export const UploadProgressBar: React.FC = () => {
  const navigate = useNavigate();
  const { isUploading, activeCount, completedCount, errorCount, overallProgress, items } = useUploadContext();

  // Nothing in flight and no failures to surface — stay out of the toolbar.
  if (!isUploading && errorCount === 0) {
    return null;
  }

  const total = items.length;
  const label = isUploading
    ? `Uploading ${completedCount + errorCount}/${total} files`
    : `${errorCount} upload${errorCount === 1 ? '' : 's'} failed`;

  return (
    <Tooltip title={isUploading
      ? `${activeCount} file(s) in progress — uploads continue while you navigate`
      : 'Some files failed to upload. Click for details.'}>
      <ButtonBase
        onClick={() => navigate('/upload')}
        aria-label={`${label}. Open upload page for details.`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          border: 1,
          borderColor: errorCount > 0 && !isUploading ? 'error.main' : 'divider',
        }}
      >
        {isUploading ? (
          <CloudUpload fontSize="small" color="primary" />
        ) : (
          <ErrorOutline fontSize="small" color="error" />
        )}
        <Box sx={{ minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="left">
            {label}
          </Typography>
          {isUploading && (
            <LinearProgress
              variant="determinate"
              value={overallProgress}
              sx={{ height: 4, borderRadius: 1 }}
            />
          )}
        </Box>
      </ButtonBase>
    </Tooltip>
  );
};
